import { ChevronDown, ChevronUp } from "baseui/icon";
import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { IoTimeOutline } from "react-icons/io5";
import { LuBriefcase, LuCreditCard, LuPencil, LuTrash2 } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import * as devConfig from "../../../app/configs/devConfig";
import { getAuthHeader } from "../../../app/globalState/GlobalAuthState";
import { LottieLoading } from "../../../components/graphics/LottieLoading";
import { getInitials } from "../../../constants/constants";
import { Attachment } from "../../../factories/emails/models/send-email.model";
import { useAppSelector } from "../../../redux/redux";
import {
  IInvoiceCustomers,
  InvoiceItem,
  SortEnum,
  useDeleteInvoiceMutation,
  useGetBillingLogoQuery,
  useGetInvoiceByIdQuery,
  useGetInvoiceStatusesQuery,
  useGetPaymentByInvoiceIdQuery,
  useGetPaymentsRecievedQuery,
  useGetTaxRateQuery,
  useSetInvoiceLabelMutation,
} from "../../../services/BillingService";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { IFirm } from "../../../types/types";
import { InvoicePortalShare } from "../../clientPortal/InvoicePortalShare";
import { ComposeEmailModal } from "../../contacts/modals/ComposeEmailModal";
import { navigateToContact } from "../../contacts/utils/navigateToContacts";
import DeletePopup from "../DeletePopup";
import DropStatus from "../DropStatus";
import PayInvoiceModal from "../payments/PayInvoiceModal";
import {
  ActionsMenu,
  BILLING_CARD,
  BILLING_PRIMARY_BUTTON,
  BillingClient,
  ClientAvatar,
  deriveInvoiceStatus,
  formatMoney,
  InvoiceClients,
  invoiceStatusBadgeBg,
  invoiceStatusColor,
} from "../ui/BillingUi";
import { invoiceStatusLabel } from "../ui/invoiceStatus.utils";
import PDFPreview from "./generatePDF";
import {
  findTaxCode,
  invoiceTaxSummary,
  SAVED_TAX_HINT,
  savedTaxNote,
} from "./invoiceTax.utils";
import { ViewWork } from "./ViewWork";

const discountTypes = {
  "%": "89EDC863-9055-479B-AD08-554C0418A59F",
  $: "771B703A-5E94-4271-988A-206C0B48F7A8",
};

const InvoiceDetails = ({
  setView,
}: {
  setView: React.Dispatch<React.SetStateAction<"details" | "edit">>;
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.appReducer);

  const [sort, setSort] = useState<{ name: SortEnum; order: boolean }>({
    name: SortEnum.PaymentDate,
    order: false,
  });
  const [isOpenPay, setIsOpenPay] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [viewWork, setViewWork] = useState(false);
  const [workItem, setWorkItem] = useState<InvoiceItem>();
  const [collapsedItems, setCollapsedItems] = useState<string[]>([]);
  const [isOpenCompose, setIsOpenCompose] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<Attachment>({
    content_type: "",
    filename: "",
    content: "",
  });

  const {
    data: invoice,
    isLoading: isLoadingInvoice,
    isFetching,
  } = useGetInvoiceByIdQuery(`${id}`);

  const { data: logo, isLoading: isLoadingLogo } = useGetBillingLogoQuery(
    getLinkedFirm()?.orgId || "",
  );

  const { data: statuses, isLoading: isLoadingStatuses } =
    useGetInvoiceStatusesQuery(getLinkedFirm()?.orgId || "");

  const [updateInvoice, { isLoading: isLoadingUpdate }] =
    useSetInvoiceLabelMutation();

  const {
    data: payments,
    isLoading: isLoadingPayments,
    refetch,
  } = useGetPaymentByInvoiceIdQuery(invoice?.id || "", {
    skip: !invoice?.id,
  });

  const { data: paymentsRecieved, isLoading: isLoadingPaymentsRecieved } =
    useGetPaymentsRecievedQuery(`${id}`);

  const { data: taxCodeData, isLoading: isLoadingTaxRate } = useGetTaxRateQuery(
    getLinkedFirm()?.orgId,
  );

  const [deleteInvoice, { isSuccess: isSuccessDelete }] =
    useDeleteInvoiceMutation();

  const [firm, setFirm] = useState<IFirm | null>(null);
  const [isLoadingFirm, setIsLoadingFirm] = useState(false);

  const handleSort = (name: SortEnum) => {
    if (name === sort.name) {
      setSort({ name, order: !sort.order });
    } else {
      setSort({ name, order: true });
    }
  };
  const totalRate = (value: string) => {
    const hours = Number(value.split("h").slice(0, -1));
    const minutes = Number(value?.split("h")?.pop()?.replace("m", "")) / 60;
    return hours + minutes;
  };
  const getFirm = async () => {
    const response = await fetch(
      `${devConfig.getServerBaseUrlForEnv()}/api/org/${getLinkedFirm()?.orgId}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: getAuthHeader().Authorization,
        },
        method: "GET",
      },
    );
    if (response.ok) {
      const data = await response.json();
      setFirm(data);
    }
    setIsLoadingFirm(false);
  };

  const subtotal = useMemo(() => {
    let value = 0;
    invoice?.invoiceItem?.forEach((item) => (value += item.amount));
    return value;
  }, [invoice]);

  const discountValue = useMemo(() => {
    if (invoice?.discountTypeId === discountTypes.$.toLowerCase()) {
      return invoice?.discount || 0;
    }
    if (invoice?.discountTypeId === discountTypes["%"].toLowerCase()) {
      return (subtotal * (invoice?.discount || 0)) / 100;
    }
    return 0;
  }, [invoice, subtotal]);

  const paymentsItems = useMemo(() => {
    const result: any[] = [];
    if (payments) {
      payments.forEach((payment) => {
        const i = payment.paymentItem.find(
          (item) => item.invoiceId === invoice?.id,
        );
        if (i) {
          result.push({
            ...i,
            customer: payment?.customer,
            customerId: payment?.customerId,
            paymentMethod: payment?.paymentMethod,
            note: payment?.notes,
            paymentDate: payment?.paymentDate,
          });
        }
      });
    }
    return result;
  }, [payments]);

  // Only the label changes (QA batch 12): resending the invoice cleared its notes.
  const updateStatus = (statusId: string) => {
    updateInvoice({ id: invoice?.id || "", labelId: statusId || null });
  };

  useEffect(() => {
    if (invoice?.id) {
      refetch();
    }
  }, [invoice]);

  useEffect(() => {
    if (isSuccessDelete) {
      navigate("/billing/invoices");
    }
  }, [isSuccessDelete]);

  useEffect(() => {
    setIsLoadingFirm(true);
    getFirm();
  }, []);

  if (
    isLoadingInvoice ||
    isLoadingLogo ||
    isLoadingTaxRate ||
    isLoadingPaymentsRecieved ||
    isLoadingPayments ||
    isLoadingFirm ||
    isLoadingStatuses
  ) {
    return <LottieLoading />;
  }

  // BILL-039 / DATA-001 (QA batch 17): each line with its own tax code; when
  // today's rates don't give the saved total, show the tax as saved.
  const taxSummary = invoiceTaxSummary({
    items: (invoice?.invoiceItem || []).map((item) => ({
      amount: item.amount || 0,
      taxCodeId: item.taxCodeId,
    })),
    taxCodes: taxCodeData,
    discountValue,
    isIncludeTax: !!invoice?.isIncluded,
  });
  const savedTax = savedTaxNote(
    taxSummary,
    invoice?.total,
    !!invoice?.isIncluded,
  );
  const pdfTaxRows = savedTax
    ? [{ label: "Sales tax (as saved)", amount: savedTax.tax }]
    : taxSummary.rows.map((row) => ({
        label: `${row.taxName} (${row.taxRate}%)`,
        amount: row.amount,
      }));
  const lineTaxCode = (taxCodeId?: string | null) =>
    findTaxCode(taxCodeData, taxCodeId);
  const lineTaxRate = (taxCodeId?: string | null) =>
    (lineTaxCode(taxCodeId)?.taxRates || []).reduce(
      (sum, item) => sum + (item.taxRate || 0),
      0,
    );
  const balanceDue = (invoice?.total || 0) - (paymentsRecieved || 0);
  const status = deriveInvoiceStatus({
    status: invoice?.status,
    total: invoice?.total,
    dueDate: invoice?.dueDate,
    isPayed: invoice?.isPayed,
    balanceDue,
  });
  const hasBilledItems = !!invoice?.invoiceItem?.some(
    (item) => item.invoiceType === "billedItem",
  );
  const itemGrid = hasBilledItems
    ? "grid-cols-[40px_1.1fr_1.4fr_70px_110px_110px_140px_32px]"
    : "grid-cols-[40px_1.1fr_1.4fr_70px_110px_110px_140px]";

  const clients: (BillingClient & {
    customerId?: string;
    businessContactId?: string;
  })[] = [
    {
      id: invoice?.customer?.id,
      customerId: invoice?.customer?.id,
      businessContactId: invoice?.customer?.businessContactId || "",
      name: invoice?.customer?.name || "",
      isOrganization: !!invoice?.customer?.businessContactId,
    },
    ...(invoice?.invoiceCustomers || []).map((item: IInvoiceCustomers) => ({
      id: item?.id,
      customerId: item?.customerId,
      businessContactId: item?.customers?.businessContactId || "",
      name: item?.customers?.name || "",
      isOrganization: !!item?.customers?.businessContactId,
    })),
  ].filter((client) => client.name);

  const openClient = (client: BillingClient) => {
    const match = clients.find((item) => item.id === client.id);
    if (!match) return;
    navigate(
      navigateToContact({
        type: match.businessContactId ? "organization" : "contact",
        customerId: match.customerId || "",
        id: match.businessContactId || "",
      }),
    );
  };

  const summaryRow = (label: string, value: string) => (
    <div className={"mb-2 flex justify-between text-[13.5px] text-[#3A4256]"}>
      <span className={"text-[#6B7185]"}>{label}</span>
      <span className={"tabular-nums"}>{value}</span>
    </div>
  );

  return (
    <>
      <div className={classNames(BILLING_CARD, "relative mb-5 px-7 py-[26px]")}>
        <div className={"flex items-start justify-between gap-4"}>
          <div>
            {status && (
              <span
                className={
                  "mb-3.5 inline-block rounded-[20px] px-3 py-1 text-xs font-bold"
                }
                style={{
                  color: invoiceStatusColor(status),
                  backgroundColor: invoiceStatusBadgeBg(status),
                }}
              >
                {invoiceStatusLabel(status)}
              </span>
            )}
            <h2
              className={
                "m-0 text-2xl font-extrabold tracking-[-0.5px] text-[#1F2433]"
              }
            >
              Invoice No.{invoice?.invoiceNo}
            </h2>
          </div>
          <div className={"flex items-center gap-2.5"}>
            <DropStatus
              isLoading={isLoadingUpdate || isFetching}
              invoiceStatus={invoice?.invoiceStatus || null}
              paymentStatus={null}
              statuses={statuses || []}
              selectStatus={(selected) => {
                updateStatus(selected?.id || "");
              }}
            />
            <ActionsMenu
              items={[
                {
                  label: "Edit",
                  icon: <LuPencil size={15} />,
                  onClick: () => setView("edit"),
                },
                {
                  label: "Pay",
                  icon: <LuCreditCard size={15} />,
                  onClick: () => setIsOpenPay(true),
                },
                {
                  label: "Delete",
                  icon: <LuTrash2 size={15} />,
                  danger: true,
                  onClick: () => {
                    if (invoice) {
                      setIsOpenDelete(true);
                    }
                  },
                },
              ]}
            />
          </div>
        </div>

        <div className={"mb-2 mt-5 text-xs font-bold text-[#9097A8]"}>
          {clients.length > 1 ? "Clients" : "Client"}
        </div>
        <InvoiceClients clients={clients} onClientClick={openClient} />

        <div className={"mt-[22px] flex flex-wrap gap-x-[60px] gap-y-4"}>
          {invoice?.assignedToUser && (
            <div>
              <div className={"mb-[7px] text-xs font-bold text-[#9097A8]"}>
                Assignee
              </div>
              <div className={"flex items-center gap-2"}>
                <span
                  className={
                    "flex h-[26px] w-[26px] flex-none items-center justify-center rounded-full text-[10.5px] font-bold text-white"
                  }
                  style={{
                    backgroundColor:
                      invoice?.assignedToUser?.userProfile?.color || "#1E3A8A",
                  }}
                >
                  {getInitials(
                    invoice?.assignedToUser?.userProfile?.firstName || "",
                    invoice?.assignedToUser?.userProfile?.lastName || "",
                  )}
                </span>
                <span className={"text-sm text-[#1F2433]"}>
                  {`${invoice?.assignedToUser?.userProfile?.firstName} ${invoice?.assignedToUser?.userProfile?.lastName}`}
                </span>
              </div>
            </div>
          )}
          {[
            {
              label: "Invoice Date",
              value: moment(invoice?.invoiceDate).format("MMM DD, YYYY"),
            },
            {
              label: "Terms",
              value: invoice?.invoiceTerms?.termName || "N/A",
            },
            {
              label: "Due Date",
              value: moment(invoice?.dueDate).format("MMM DD, YYYY"),
            },
          ].map((field) => (
            <div key={field.label}>
              <div className={"mb-[7px] text-xs font-bold text-[#9097A8]"}>
                {field.label}
              </div>
              <div className={"pt-[3px] text-sm text-[#1F2433]"}>
                {field.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={classNames(BILLING_CARD, "px-7 pb-7 pt-6")}>
        <h3 className={"m-0 mb-4 text-[17px] font-extrabold text-[#1F2433]"}>
          Service(s)
        </h3>
        <div
          className={classNames(
            "grid items-center gap-2.5 rounded bg-[#EAECEF] px-4 py-[11px] text-[12.5px] font-bold text-[#727A8A]",
            itemGrid,
          )}
        >
          <span>#</span>
          <span>Service</span>
          <span>Description</span>
          <span>Qty</span>
          <span>Rate</span>
          <span>Amount</span>
          <span>Sales Tax</span>
          {hasBilledItems && <span />}
        </div>
        {invoice?.invoiceItem?.map((item, index) => (
          <div
            key={item.id || index}
            className={classNames(
              "grid items-center gap-2.5 border-b border-[#F4F5F7] px-4 py-[15px] text-[13.5px] text-[#3A4256]",
              itemGrid,
            )}
          >
            <span className={"text-[#9097A8]"}>{index + 1}</span>
            <span className={"font-semibold text-[#1F2433]"}>
              {item?.billingService?.name || "N/A"}
            </span>
            <span className={"break-words text-[#6B7185]"}>
              {item?.description || ""}
              {item.workId && (
                <button
                  type={"button"}
                  title={"Open work item"}
                  onClick={() => navigate(`/work/${item.workId}`)}
                  className={
                    "mt-1 flex max-w-full items-center gap-1 rounded-md bg-[#F4F2FF] px-2 py-0.5 text-left text-[11.5px] font-bold text-[#5B3FE0] hover:bg-[#EAE5FF]"
                  }
                >
                  <LuBriefcase size={12} className={"shrink-0"} />
                  <span className={"truncate"}>
                    {item.work?.name || "Work item"}
                  </span>
                </button>
              )}
            </span>
            <span>{item?.quantity || 0}</span>
            <span className={"tabular-nums"}>{formatMoney(item?.rate)}</span>
            <span className={"tabular-nums"}>{formatMoney(item?.amount)}</span>
            <span className={"inline-flex items-center gap-[7px]"}>
              {lineTaxCode(item.taxCodeId) && (
                <>
                  <span className={"text-[12.5px] text-[#6B7185]"}>
                    {lineTaxCode(item.taxCodeId)?.taxCodeName}
                  </span>
                  {lineTaxRate(item.taxCodeId) > 0 && (
                    <span
                      className={
                        "rounded-md bg-[#F1F2F5] px-2 py-0.5 text-[11.5px] font-bold text-[#3A4256]"
                      }
                    >
                      {lineTaxRate(item.taxCodeId)}%
                    </span>
                  )}
                </>
              )}
            </span>
            {hasBilledItems && (
              <span>
                {item.invoiceType === "billedItem" && (
                  <>
                    <button
                      type={"button"}
                      aria-label={"View billed time"}
                      onClick={() => {
                        setWorkItem(item);
                        setViewWork(true);
                      }}
                      className={"text-[#6B4EFF] hover:text-[#5A3EEB]"}
                    >
                      <IoTimeOutline size={20} />
                    </button>
                    {viewWork && workItem?.id === item.id && (
                      <ViewWork
                        billedWork={workItem}
                        isActive={viewWork}
                        setCollapsedItems={setCollapsedItems}
                        collapsedItems={collapsedItems}
                        setViewWork={setViewWork}
                        totalRate={totalRate}
                      />
                    )}
                  </>
                )}
              </span>
            )}
          </div>
        ))}

        <div
          className={"mt-[26px] grid grid-cols-[1fr_340px] items-start gap-10"}
        >
          <div>
            {!!invoice?.notes?.trim() && (
              <>
                <div
                  className={"mb-[9px] text-[13px] font-bold text-[#3A4256]"}
                >
                  Notes
                </div>
                <div
                  className={
                    "max-w-[380px] whitespace-pre-line break-words rounded-[10px] border border-[#EAECEF] bg-[#FCFCFD] px-3.5 py-3 text-xs leading-[1.55] text-[#6B7185]"
                  }
                >
                  {invoice?.notes}
                </div>
              </>
            )}
          </div>
          <div>
            <div className={"mb-3.5 text-[17px] font-extrabold text-[#1F2433]"}>
              Summary
            </div>
            {summaryRow("Subtotal", formatMoney(subtotal))}
            {summaryRow("Discount", formatMoney(discountValue))}
            {savedTax ? (
              <div title={SAVED_TAX_HINT}>
                <div className={"mb-1.5 mt-3 text-[12.5px] text-[#9097A8]"}>
                  Sales Tax
                </div>
                <div
                  className={
                    "mb-1.5 flex justify-between pl-2.5 text-[13px] text-[#3A4256]"
                  }
                >
                  <span className={"text-[#6B7185]"}>As saved</span>
                  <span className={"tabular-nums"}>
                    {formatMoney(savedTax.tax)}
                  </span>
                </div>
                <div className={"mb-1.5 pl-2.5 text-xs text-[#9097A8]"}>
                  {SAVED_TAX_HINT}
                </div>
              </div>
            ) : (
              !!taxSummary.rows.length && (
                <>
                  <div className={"mb-1.5 mt-3 text-[12.5px] text-[#9097A8]"}>
                    Sales Tax
                  </div>
                  {taxSummary.rows.map((row) => (
                    <div
                      key={row.key}
                      className={
                        "mb-1.5 flex justify-between pl-2.5 text-[13px] text-[#3A4256]"
                      }
                    >
                      <span className={"text-[#6B7185]"}>
                        {row.taxName} ({row.taxRate}%)
                      </span>
                      <span className={"tabular-nums"}>
                        {formatMoney(row.amount)}
                      </span>
                    </div>
                  ))}
                  {invoice?.isIncluded && (
                    <div className={"mb-1.5 pl-2.5 text-xs text-[#9097A8]"}>
                      Included in the amounts (on{" "}
                      {formatMoney(taxSummary.taxBaseAmount)})
                    </div>
                  )}
                </>
              )
            )}
            <div
              className={
                "mt-3 flex items-center justify-between border-t border-[#EAECEF] pt-3.5"
              }
            >
              <span className={"text-[15px] font-extrabold text-[#1F2433]"}>
                Total
              </span>
              <span
                className={
                  "text-[17px] font-extrabold tabular-nums text-[#1F2433]"
                }
              >
                {formatMoney(invoice?.total)}
              </span>
            </div>
            <div
              className={"mt-2 flex justify-between text-[13px] text-[#6B7185]"}
            >
              <span>Payment Received</span>
              <span className={"tabular-nums"}>
                {formatMoney(paymentsRecieved)}
              </span>
            </div>
            <div className={"mt-3.5 flex items-center justify-between"}>
              <span className={"text-base font-extrabold text-[#1F2433]"}>
                Balance Due
              </span>
              <span
                className={
                  "text-[22px] font-extrabold tabular-nums text-[#6B4EFF]"
                }
              >
                {formatMoney(balanceDue)}
              </span>
            </div>
            <div className={"mt-5"}>
              <PDFPreview
                variant={"billing"}
                invoice={invoice}
                firm={firm}
                logo={logo}
                paymentsRecieved={paymentsRecieved || 0}
                taxName={lineTaxCode(invoice?.invoiceItem?.[0]?.taxCodeId)?.taxCodeName || ""}
                taxRates={
                  lineTaxCode(invoice?.invoiceItem?.[0]?.taxCodeId)?.taxRates?.map(
                    (item) => item.taxRate,
                  ) || []
                }
                taxRows={pdfTaxRows}
                taxBase={taxSummary.taxBaseAmount}
                setInvoiceFile={setInvoiceFile}
                setIsOpenCompose={setIsOpenCompose}
              />
            </div>
            <button
              type={"button"}
              onClick={() => setIsOpenPay(true)}
              className={classNames(
                BILLING_PRIMARY_BUTTON,
                "mt-2.5 h-11 w-full text-sm",
              )}
            >
              <LuCreditCard size={17} />
              Pay Invoice
            </button>
            <InvoicePortalShare
              invoiceId={invoice?.id}
              invoiceNo={invoice?.invoiceNo}
            />
          </div>
        </div>
      </div>

      <div className={"mt-5 text-[12.5px] text-[#9097A8]"}>
        {moment(invoice?.createdAt).format("MMM DD, YYYY")} Invoice created by{" "}
        {invoice?.createdByUser &&
          `${invoice?.createdByUser?.userProfile?.firstName} ${invoice?.createdByUser?.userProfile?.lastName}`}
      </div>

      {Array.isArray(payments) && payments.length > 0 && (
        <div className={classNames(BILLING_CARD, "mt-5 overflow-hidden")}>
          <h3
            className={
              "m-0 px-[22px] pb-3 pt-5 text-[17px] font-extrabold text-[#1F2433]"
            }
          >
            Payments
          </h3>
          <table className={"w-full"}>
            <thead
              className={
                "border-y border-[#F0F1F4] text-left text-[12.5px] font-bold text-[#8B92A2]"
              }
            >
              <tr>
                {[
                  { label: "Contact", sort: SortEnum.Customer },
                  { label: "Payment date", sort: SortEnum.PaymentDate },
                  { label: "Amount", sort: SortEnum.Amount },
                  { label: "Method", sort: SortEnum.PaymentMethod },
                  { label: "Notes", sort: SortEnum.Notes },
                ].map((column) => (
                  <th
                    key={column.label}
                    className={
                      "px-2.5 py-2.5 font-bold first:pl-[22px] last:pr-[22px]"
                    }
                  >
                    <button
                      type={"button"}
                      onClick={() => handleSort(column.sort)}
                      className={"flex items-center whitespace-nowrap"}
                    >
                      {column.label}
                      {sort?.name === column.sort && (
                        <span className={"ml-0.5 text-[#6B4EFF]"}>
                          {sort?.order ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </span>
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(paymentsItems ?? []).map((payment, index) => (
                <tr
                  key={index}
                  className={
                    "border-b border-[#F4F5F7] text-[13px] text-[#3A4256] last:border-none hover:bg-[#FAFAFC]"
                  }
                >
                  <td className={"py-3.5 pl-[22px] pr-2.5"}>
                    <button
                      type={"button"}
                      onClick={(e) => {
                        e.stopPropagation();
                        openClient(clients[0]);
                      }}
                      className={"flex items-center gap-2.5"}
                    >
                      {clients[0] && <ClientAvatar client={clients[0]} />}
                      <span
                        className={
                          "text-[13.5px] font-semibold text-[#1F2433] hover:underline"
                        }
                      >
                        {invoice?.customer?.name}
                      </span>
                    </button>
                  </td>
                  <td className={"px-2.5 py-3.5"}>
                    {moment(payment?.paymentDate).format("MMM DD, YYYY")}
                  </td>
                  <td className={"px-2.5 py-3.5 tabular-nums"}>
                    {formatMoney(payment?.amountPaid)}
                  </td>
                  <td className={"px-2.5 py-3.5"}>
                    {payment?.paymentMethod?.paymentMethod}
                  </td>
                  <td className={"py-3.5 pl-2.5 pr-[22px] text-[#8B92A2]"}>
                    {payment?.note || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isOpenCompose &&
      (invoice?.customer?.businessContact?.emails?.[0]?.emailAddress ||
        invoice?.customer?.contact?.email?.[0]?.emailAddress) ? (
        <ComposeEmailModal
          isActive={isOpenCompose}
          onClose={() => setIsOpenCompose(false)}
          bulk={true}
          invoiceFile={invoiceFile}
          invoiceEmail={
            invoice?.customer?.contact?.email?.[0]?.emailAddress ||
            invoice?.customer?.businessContact?.emails?.[0]?.emailAddress
          }
        />
      ) : (
        <ComposeEmailModal
          isActive={isOpenCompose}
          invoiceFile={invoiceFile}
          onClose={() => setIsOpenCompose(false)}
          bulk={true}
        />
      )}
      {isOpenPay && (
        <PayInvoiceModal close={() => setIsOpenPay(false)} invoice={invoice} />
      )}
      {isOpenDelete && (
        <DeletePopup
          onClose={() => setIsOpenDelete(false)}
          isActive={isOpenDelete}
          onDelete={() => {
            if (invoice) {
              deleteInvoice(invoice);
            }
            setIsOpenDelete(false);
          }}
        />
      )}
    </>
  );
};

export default InvoiceDetails;
